/** 客诉任务管理端 / 作业端（与 api-server complainttask 模块对齐）。时间均为 ISO-8601 UTC 字符串，按浏览器本地时区显示。 */

export type ComplaintTaskStatus = 20 | 40 | 70;

export type ComplaintTaskStage = 'UNRESPONDED' | 'IN_PROGRESS' | 'RESOLVED';

export type ComplaintOverdue = 'RESPONSE' | 'RESOLVE';

export interface ComplaintTaskRow {
  taskId: number;
  taskNo?: string | null;
  subType?: string | null;
  subTypeName?: string | null;
  /** 1 高 / 2 中 / 3 低 */
  priority?: number | null;
  /** NORMAL / MILD / BURST */
  emotionLevel?: string | null;
  customerId?: number | null;
  customerName?: string | null;
  customerLevel?: string | null;
  chatName?: string | null;
  warehouseId?: number | null;
  warehouseName?: string | null;
  ownerHubId?: number | null;
  ownerHubName?: string | null;
  title?: string | null;
  trackingNos: string[];
  status?: number | null;
  startAt?: string | null;
  assignedAt?: string | null;
  actualStartAt?: string | null;
  finishAt?: string | null;
  responseDueAt?: string | null;
  resolveDueAt?: string | null;
  responseBreached?: boolean | null;
  resolveBreached?: boolean | null;
  assigneeAccountId?: number | null;
  assigneeName?: string | null;
  assigneeRole?: string | null;
  /** 1 系统自动分发 / 2 人工指派 */
  assignMode?: number | null;
  /** 当前登录人能否指派 / 转派（仅管理列表返回） */
  canAssign?: boolean | null;
}

export interface ComplaintTaskPage {
  list: ComplaintTaskRow[];
  pageNum: number;
  pageSize: number;
  total: number;
  serverNow?: string | null;
}

export interface ComplaintTaskCount {
  key: string;
  count: number;
}

export interface ComplaintTaskFacet {
  total: number;
  byStatus: ComplaintTaskCount[];
  responseOverdue: number;
  resolveOverdue: number;
  byPriority: ComplaintTaskCount[];
  bySubType: ComplaintTaskCount[];
  byHub: ComplaintTaskCount[];
}

export interface ComplaintMessage {
  speaker?: string | null;
  content?: string | null;
  sentAt?: string | null;
  isCustomer?: boolean | null;
}

export interface ComplaintTaskLog {
  action?: string | null;
  fromValue?: string | null;
  toValue?: string | null;
  reason?: string | null;
  operatorType?: number | null;
  operatorName?: string | null;
  occurredAt?: string | null;
  extraJson?: string | null;
}

export interface ComplaintAttachment {
  id?: number | null;
  fileName?: string | null;
  contentType?: string | null;
  url?: string | null;
}

export interface ComplaintTaskDetail {
  task: ComplaintTaskRow;
  messages: ComplaintMessage[];
  logs: ComplaintTaskLog[];
  resolutionContent?: string | null;
  resolvedBy?: string | null;
  resolvedAt?: string | null;
  attachments: ComplaintAttachment[];
  serverNow?: string | null;
  canAssign: boolean;
  canProcess: boolean;
}

export interface ComplaintTaskCandidate {
  accountId: number;
  name: string;
  /** 为空 = 不在客诉名单（Lark 下属或本人） */
  roleCode: string;
  hubId?: number | null;
  /** 是否当前登录人 */
  self: boolean;
}

/** 承接人列表层级（按优先级取第一个命中的） */
export type ComplaintOperatorTier = 'SYSTEM_ADMIN' | 'HUB_MANAGER' | 'SUPERVISOR' | 'SELF';

export interface ComplaintTaskContext {
  accountId: number;
  name: string;
  systemAdmin: boolean;
  manager: boolean;
  tier: ComplaintOperatorTier;
  hubOptions: {id: number; name: string}[];
}

export interface ComplaintTaskFilterInput {
  startFrom?: string;
  startTo?: string;
  statuses?: number[];
  overdue?: ComplaintOverdue;
  hubIds?: number[];
  priorities?: number[];
  subTypes?: string[];
  assigneeId?: number;
}

export interface ComplaintAttachmentInput {
  fileKey: string;
  fileName: string;
  contentType?: string;
}

/** 17 类客诉分类码（与 basic ComplaintSubType 对齐），文案见 i18n complaintTask.subType.* */
export const COMPLAINT_SUB_TYPES = [
  'PICKUP_URGE', 'ONLINE_URGE', 'DELIVERY_URGE', 'DNR_POD_INVALID', 'POD_VALID', 'LOST', 'DAMAGED',
  'CLAIM_INELIGIBLE', 'SERVICE_ATTITUDE', 'VIOLENT_DELIVERY', 'DRIVER_THEFT', 'PROPERTY_DAMAGE', 'PRIVACY',
  'OPERATION_VIOLATION', 'INFO_MODIFY', 'RETURN', 'INFO_QUERY',
] as const;
